export type DashboardDailyActivity = {
	transactions: number
	spend: number
}

export type DashboardTransactionStatusSummary = {
	successful: number
	declined: number
	pending: number
	other: number
	resolved: number
	total: number
	successRate: number
	declineRate: number
}

export type DashboardStatusTrendPoint = {
	date: string
	name: string
	fullDate: string
	successful: number
	declined: number
	pending: number
	other: number
}

export const getDashboardPeriodMetricLabel = (period: "month" | "week" | "day") => {
	if (period === "month") return "Spent this month"
	if (period === "week") return "Spent this week"
	return "Spent today"
}

export const sumDashboardCardBalances = (
	cards: Pick<ICardSaved, "balance">[]
) => cards.reduce((total, card) => total + (Number(card.balance) || 0), 0)

type DashboardTransaction = Pick<
	ICompanyTrxTransaction,
	"amount" | "card_uid" | "created_at" | "description" | "direction" | "status"
>

const NON_SPEND_DESCRIPTION =
	/(?:fee|topup|top up|withdrawal|balance return|balance transfer|issuance|activation)/i
const ISO_DATE_LENGTH = 10
const DATE_PART_PADDING = 2
const CALENDAR_MIDDAY_HOUR = 12

export const toDashboardDateKey = (value: string | Date) => {
	const date = value instanceof Date ? value : new Date(value)
	if (Number.isNaN(date.getTime())) {
		return typeof value === "string" ? value.slice(0, ISO_DATE_LENGTH) : ""
	}

	const year = date.getFullYear()
	const month = String(date.getMonth() + 1).padStart(DATE_PART_PADDING, "0")
	const day = String(date.getDate()).padStart(DATE_PART_PADDING, "0")
	return `${year}-${month}-${day}`
}

export const buildDashboardDateKeys = (days: number, endDate = new Date()) => {
	const normalizedDays = Math.max(0, Math.floor(days))
	const calendarEnd = new Date(endDate)
	calendarEnd.setHours(CALENDAR_MIDDAY_HOUR, 0, 0, 0)

	return Array.from({ length: normalizedDays }, (_, index) => {
		const date = new Date(calendarEnd)
		date.setDate(date.getDate() - (normalizedDays - index - 1))
		return toDashboardDateKey(date)
	})
}

export const isNoLimitTransactionResponseIncomplete = (
	response?: Pick<ICompanyTransactionsResponse, "pagination" | "transactions" | "truncated"> | null
) => {
	if (!response) return false
	if (response.truncated === true) return true

	const total = Number(response.pagination?.total)
	return Number.isFinite(total) && total > response.transactions.length
}

const isSettledCardSpend = (transaction: DashboardTransaction) =>
	Boolean(transaction.card_uid) &&
	transaction.direction === "expense" &&
	transaction.status.toLowerCase() === "completed" &&
	!NON_SPEND_DESCRIPTION.test(transaction.description || "")

export const buildDashboardDailyActivity = (transactions: DashboardTransaction[]) => {
	const activity = new Map<string, DashboardDailyActivity>()

	transactions.forEach((transaction) => {
		const date = toDashboardDateKey(transaction.created_at)
		const current = activity.get(date) || { transactions: 0, spend: 0 }
		current.transactions += 1
		if (isSettledCardSpend(transaction)) current.spend += Number(transaction.amount) || 0
		activity.set(date, current)
	})

	return activity
}

export const countDashboardTransactionsByBuyer = (
	transactions: Pick<ICompanyTrxTransaction, "buyer_uid">[]
) => {
	const counts = new Map<string, number>()
	transactions.forEach((transaction) => {
		if (!transaction.buyer_uid) return
		counts.set(transaction.buyer_uid, (counts.get(transaction.buyer_uid) || 0) + 1)
	})
	return counts
}

export const isDashboardDeclinedStatus = (status: string) => {
	const normalized = status.trim().toLowerCase()
	return normalized === "error" || normalized === "declined" || normalized === "failed"
}

const getDashboardStatusBucket = (
	status: string
): keyof Pick<
	DashboardTransactionStatusSummary,
	"successful" | "declined" | "pending" | "other"
> => {
	const normalized = status.trim().toLowerCase()
	if (normalized === "completed" || normalized === "success") return "successful"
	if (isDashboardDeclinedStatus(normalized)) return "declined"
	if (normalized === "pending") return "pending"
	return "other"
}

export const buildDashboardStatusTrend = (
	transactions: Pick<ICompanyTrxTransaction, "created_at" | "status">[],
	dateKeys: string[]
): DashboardStatusTrendPoint[] => {
	const dateMap = new Map<string, DashboardStatusTrendPoint>(
		dateKeys.map((date) => {
			const pointDate = new Date(`${date}T12:00:00`)
			return [
				date,
				{
					date,
					name: pointDate.toLocaleDateString("en-GB", {
						month: "short",
						day: "numeric"
					}),
					fullDate: pointDate.toLocaleDateString("en-GB", {
						year: "numeric",
						month: "short",
						day: "numeric"
					}),
					successful: 0,
					declined: 0,
					pending: 0,
					other: 0
				}
			]
		})
	)

	transactions.forEach((transaction) => {
		const point = dateMap.get(toDashboardDateKey(transaction.created_at))
		if (!point) return
		point[getDashboardStatusBucket(transaction.status)] += 1
	})

	return dateKeys.map((date) => dateMap.get(date)!)
}

export const summarizeDashboardTransactionStatuses = (
	transactions: Pick<ICompanyTrxTransaction, "status">[]
): DashboardTransactionStatusSummary => {
	let successful = 0
	let declined = 0
	let pending = 0
	let other = 0

	transactions.forEach((transaction) => {
		const bucket = getDashboardStatusBucket(transaction.status)
		if (bucket === "successful") successful += 1
		else if (bucket === "declined") declined += 1
		else if (bucket === "pending") pending += 1
		else other += 1
	})

	const resolved = successful + declined
	return {
		successful,
		declined,
		pending,
		other,
		resolved,
		total: transactions.length,
		successRate: resolved ? (successful * 100) / resolved : 0,
		declineRate: resolved ? (declined * 100) / resolved : 0
	}
}
